import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { southAfricanProvinces, citiesByProvince } from '../mock';
import { Upload, Save, X, Camera, Trash2, User, Calendar, Award, CreditCard, AlertCircle, CheckCircle, QrCode, Download, RefreshCw } from 'lucide-react';
import axios from 'axios';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(
    user?.profile_image ? `${backendUrl}/api/user/profile/image/${user.id}` : null
  );
  const [myEvents, setMyEvents] = useState([]);
  const [cpdData, setCpdData] = useState({ cpd_records: [], user_points: [], total_points: 0 });
  const [payments, setPayments] = useState([]);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [qrModal, setQrModal] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    preferred_designation: user?.preferred_designation || '',
    alternate_contact: user?.alternate_contact || '',
    country: user?.country || 'South Africa',
    province: user?.province || '',
    city: user?.city || '',
    public_email: user?.public_email || '',
    public_phone: user?.public_phone || '',
    public_address_line1: user?.public_address_line1 || '',
    public_address_line2: user?.public_address_line2 || '',
    practice_name: user?.practice_name || '',
    ahpcsa_number: user?.ahpcsa_number || '',
    bhf_number: user?.bhf_number || '',
    student_number: user?.student_number || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'province' ? { city: '' } : {})
    }));
  };

  useEffect(() => {
    if (activeTab === 'events') fetchMyEvents();
    if (activeTab === 'achievements') fetchCpdPoints();
    if (activeTab === 'payment') fetchPayments();
  }, [activeTab]);

  useEffect(() => {
    fetchMembershipStatus();
  }, []);

  const fetchMembershipStatus = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/user/membership-status`);
      setMembershipStatus(response.data);
    } catch (error) {
      console.error('Error fetching membership status:', error);
    }
  };

  const fetchMyEvents = async () => {
    setTabLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/user/events/registered`);
      setMyEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setMyEvents([]);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchCpdPoints = async () => {
    setTabLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/user/cpd-points`);
      setCpdData(response.data || { cpd_records: [], user_points: [], total_points: 0 });
    } catch (error) {
      console.error('Error fetching CPD:', error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchPayments = async () => {
    setTabLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/user/payments`);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
    } finally {
      setTabLoading(false);
    }
  };

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const response = await axios.post(`${backendUrl}/api/payments/membership/initiate`);
      const { payfast_url, payment_data } = response.data;

      // Build a form and submit to PayFast
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payfast_url;

      Object.entries(payment_data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        }
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      toast({
        title: 'Payment Failed',
        description: error.response?.data?.detail || 'Failed to initiate payment. Please try again.',
        variant: 'destructive'
      });
      setPaying(false);
    }
  };

  const fetchQRCode = async (eventId, registrationId) => {
    try {
      setLoadingQR(true);
      const response = await axios.get(
        `${backendUrl}/api/events/registrations/${registrationId}/qr`
      );
      setQrCode(response.data);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate QR code',
        variant: 'destructive'
      });
    } finally {
      setLoadingQR(false);
    }
  };

  const handleViewQR = (event) => {
    setQrModal(event);
    setQrCode(null);
    if (event.registration_id) {
      fetchQRCode(event.id, event.registration_id);
    } else if (event.id) {
      fetchQRCode(event.event_id || event.id, event.id);
    }
  };

  const handleDownloadQR = () => {
    if (qrCode && qrCode.qr_code_image_url) {
      const dataUrl = qrCode.qr_code_image_url;
      const link = document.createElement('a');
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      link.href = URL.createObjectURL(blob);
      link.download = `qr-code-${qrModal.title || 'event'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateProfile(formData);
      if (result.success) {
        toast({
          title: 'Profile updated',
          description: 'Your profile has been updated successfully.'
        });
        setIsEditing(false);
      } else {
        toast({
          title: 'Update failed',
          description: result.error || 'Failed to update profile.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 2MB.',
        variant: 'destructive'
      });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG or PNG image.',
        variant: 'destructive'
      });
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${backendUrl}/api/user/profile/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setProfileImageUrl(`${backendUrl}${response.data.url}?t=${Date.now()}`);

      toast({
        title: 'Profile image updated',
        description: 'Your profile image has been uploaded successfully.'
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload image.',
        variant: 'destructive'
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Are you sure you want to delete your profile image?')) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/user/profile/image`);

      setProfileImageUrl(null);

      toast({
        title: 'Profile image deleted',
        description: 'Your profile image has been removed.'
      });
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Failed to delete image.',
        variant: 'destructive'
      });
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user?.full_name || '',
      alternate_contact: user?.alternate_contact || '',
      country: user?.country || 'South Africa',
      province: user?.province || '',
      city: user?.city || '',
      public_email: user?.public_email || '',
      public_phone: user?.public_phone || '',
      public_address_line1: user?.public_address_line1 || '',
      public_address_line2: user?.public_address_line2 || '',
      practice_name: user?.practice_name || '',
      ahpcsa_number: user?.ahpcsa_number || '',
      bhf_number: user?.bhf_number || '',
      student_number: user?.student_number || ''
    });
    setIsEditing(false);
  };

  const tabs = [
    { id: 'profile', label: 'MY PROFILE' },
    { id: 'events', label: 'MY EVENTS' },
    { id: 'achievements', label: 'MY ACHIEVEMENTS' },
    { id: 'payment', label: 'PAYMENT HISTORY' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="MY PROFILE" />
      
      <div className="container mx-auto px-4 py-8">
        {/* Membership Status Banner */}
        {membershipStatus && membershipStatus.renewal_needed && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            membershipStatus.status === 'expired' ? 'bg-red-50 border-red-500' :
            membershipStatus.status === 'no_payment_record' ? 'bg-yellow-50 border-yellow-500' :
            'bg-orange-50 border-orange-500'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <AlertCircle className={membershipStatus.status === 'expired' ? 'text-red-600' : 'text-yellow-600'} size={24} />
                <div>
                  <p className="font-semibold text-gray-900">
                    {membershipStatus.status === 'expired' ? 'Membership Expired' :
                     membershipStatus.status === 'no_payment_record' ? 'No Payment Record' :
                     'Membership Expiring Soon'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {membershipStatus.days_until_expiry !== null && membershipStatus.days_until_expiry < 0
                      ? `Expired ${Math.abs(membershipStatus.days_until_expiry)} days ago`
                      : membershipStatus.days_until_expiry !== null && membershipStatus.days_until_expiry <= 30
                      ? `Expires in ${membershipStatus.days_until_expiry} days`
                      : 'Please renew your membership to maintain access'}
                    {membershipStatus.price > 0 && ` - R${membershipStatus.price}`}
                  </p>
                </div>
              </div>
              <Button
                onClick={handlePayNow}
                disabled={paying}
                className="bg-[#2c3e7d] hover:bg-[#1e2a5a] whitespace-nowrap"
              >
                <CreditCard size={16} className="mr-2" />
                {paying ? 'Processing...' : 'Pay Now'}
              </Button>
            </div>
          </div>
        )}
        {membershipStatus && !membershipStatus.renewal_needed && (
          <div className="mb-6 p-4 rounded-lg border-l-4 bg-green-50 border-green-500">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <p className="font-semibold text-gray-900">Membership Active</p>
                <p className="text-sm text-gray-600">
                  {membershipStatus.days_until_expiry !== null
                    ? `Expires in ${membershipStatus.days_until_expiry} days`
                    : 'Your membership is current'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#2c3e7d] text-white rounded-t-lg'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Personal Information</h3>
                {!isEditing ? (
                  <Button onClick={() => setIsEditing(true)} className="bg-[#2c3e7d]">
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={isSaving} className="bg-[#2c3e7d]">
                      <Save size={16} className="mr-2" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button onClick={handleCancel} variant="outline">
                      <X size={16} className="mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {/* Profile Image Section */}
                <div className="flex flex-col items-center pb-6 border-b">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                      {profileImageUrl ? (
                        <img 
                          src={profileImageUrl} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                          onError={() => setProfileImageUrl(null)}
                        />
                      ) : (
                        <User className="w-16 h-16 text-gray-400" />
                      )}
                    </div>
                    {uploadingImage && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      variant="outline"
                      size="sm"
                    >
                      <Camera size={16} className="mr-2" />
                      {profileImageUrl ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    {profileImageUrl && (
                      <Button
                        onClick={handleDeleteImage}
                        disabled={uploadingImage}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">JPG or PNG. Max size 2MB.</p>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Preferred Designation</Label>
                    <Input
                      name="preferred_designation"
                      value={formData.preferred_designation}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="Dr, Mr, Mrs, Ms, etc."
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      value={user?.email}
                      disabled
                      className="mt-2 bg-gray-100"
                    />
                  </div>
                  <div>
                    <Label>Alternate Contact Number</Label>
                    <Input
                      name="alternate_contact"
                      value={formData.alternate_contact}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Membership Type</Label>
                    <Input
                      value={user?.membership_type || 'N/A'}
                      disabled
                      className="mt-2 bg-gray-100 capitalize"
                    />
                  </div>
                </div>

                {/* Public Contact Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Public Contact Information</h4>
                  <p className="text-sm text-gray-600 mb-4">This information will be visible to other members.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Email (Public)</Label>
                      <Input
                        name="public_email"
                        type="email"
                        value={formData.public_email}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Contact Number (Public) *</Label>
                      <Input
                        name="public_phone"
                        value={formData.public_phone}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Address Line 1 (Public)</Label>
                      <Input
                        name="public_address_line1"
                        value={formData.public_address_line1}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Address Line 2 (Public)</Label>
                      <Input
                        name="public_address_line2"
                        value={formData.public_address_line2}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Location</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Province</Label>
                      <Select
                        value={formData.province}
                        onValueChange={(value) => handleSelectChange('province', value)}
                        disabled={!isEditing}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select province" />
                        </SelectTrigger>
                        <SelectContent>
                          {southAfricanProvinces.map(province => (
                            <SelectItem key={province} value={province}>{province}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>City</Label>
                      <Select
                        value={formData.city}
                        onValueChange={(value) => handleSelectChange('city', value)}
                        disabled={!isEditing || !formData.province}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {formData.province && citiesByProvince[formData.province]?.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input
                        value="South Africa"
                        disabled
                        className="mt-2 bg-gray-100"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Details */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Professional Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Practice Name</Label>
                      <Input
                        name="practice_name"
                        value={formData.practice_name}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                    {user?.membership_type === 'doctor' && (
                      <>
                        <div>
                          <Label>AHPCSA Number</Label>
                          <Input
                            name="ahpcsa_number"
                            value={formData.ahpcsa_number}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label>BHF Number</Label>
                          <Input
                            name="bhf_number"
                            value={formData.bhf_number}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="mt-2"
                          />
                        </div>
                      </>
                    )}
                    {user?.membership_type === 'student' && (
                      <div>
                        <Label>Student Number</Label>
                        <Input
                          name="student_number"
                          value={formData.student_number}
                          onChange={handleChange}
                          disabled={!isEditing}
                          className="mt-2"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Events Tab */}
        {activeTab === 'events' && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">My Events</h3>
              {tabLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : myEvents.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <p className="text-gray-600">You have not registered for any events yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myEvents.map(event => (
                    <div key={event.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-semibold text-gray-900">{event.title}</h4>
                        <p className="text-sm text-gray-600">
                          {formatDate(event.event_date)} {event.location ? `| ${event.location}` : ''}
                        </p>
                        <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                          event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                          event.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {event.status || 'registered'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {event.cpd_points > 0 && (
                          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                            {event.cpd_points} CPD
                          </span>
                        )}
                        <Button
                          onClick={() => handleViewQR(event)}
                          variant="outline"
                          size="sm"
                        >
                          <QrCode size={16} className="mr-1" />
                          QR
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* My Achievements Tab */}
        {activeTab === 'achievements' && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">My Achievements</h3>
              {tabLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : (
                <>
                  <div className="mb-6 p-4 bg-[#2c3e7d] bg-opacity-5 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Total CPD Points:</span>
                      <span className="text-3xl font-bold text-[#2c3e7d]">{cpdData.total_points}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
                      <div
                        className="bg-[#2c3e7d] h-3 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(cpdData.total_points / 50 * 100, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Annual requirement: 50 CPD points</p>
                  </div>

                  {cpdData.cpd_records.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">CPD Records from Events</h4>
                      <div className="space-y-2">
                        {cpdData.cpd_records.map((rec, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Award className="text-[#2c3e7d]" size={20} />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{rec.event_title || rec.title || 'CPD Record'}</p>
                                <p className="text-xs text-gray-500">{formatDate(rec.created_at)}</p>
                              </div>
                            </div>
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                              {rec.points || 0} points
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {cpdData.user_points.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Additional Points</h4>
                      <div className="space-y-2">
                        {cpdData.user_points.map((pt, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{pt.description || pt.reason || 'Points awarded'}</p>
                              <p className="text-xs text-gray-500">{formatDate(pt.created_at)}</p>
                            </div>
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {pt.points || 0} points
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {cpdData.cpd_records.length === 0 && cpdData.user_points.length === 0 && (
                    <div className="text-center py-8">
                      <Award className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-gray-600">No CPD points or achievements recorded yet.</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment History Tab */}
        {activeTab === 'payment' && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Payment History</h3>
              {tabLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <p className="text-gray-600">No payment history found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-600">
                        <th className="pb-2 pr-4">Date</th>
                        <th className="pb-2 pr-4">Description</th>
                        <th className="pb-2 pr-4">Amount</th>
                        <th className="pb-2 pr-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-3 pr-4 text-gray-600">{formatDate(payment.created_at || payment.payment_date)}</td>
                          <td className="py-3 pr-4 text-gray-900">
                            {payment.event_title || payment.description || payment.title || 'Payment'}
                          </td>
                          <td className="py-3 pr-4 text-gray-900">
                            {payment.amount ? `R${payment.amount}` : 'N/A'}
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              (payment.payment_status || payment.status) === 'paid' || (payment.payment_status || payment.status) === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : (payment.payment_status || payment.status) === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {payment.payment_status || payment.status || 'unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* QR Code Modal */}
      {qrModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setQrModal(null)}
        >
          <Card
            className="w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <CardContent className="space-y-4 p-6">
              <h3 className="text-center text-lg font-bold text-gray-900">{qrModal.title}</h3>
              <p className="text-sm text-center text-gray-600">Your Check-In QR Code</p>
              {loadingQR ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : qrCode ? (
                <>
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200 flex justify-center">
                    <img
                      src={qrCode.qr_code_image_url}
                      alt="QR Code"
                      className="w-64 h-64 object-contain"
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600">
                      Show this QR code at the event entrance for check-in
                    </p>
                    {qrCode.expires_at && (
                      <p className="text-xs text-gray-500">
                        Valid until: {new Date(qrCode.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleDownloadQR} variant="outline" className="flex-1">
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                    <Button
                      onClick={() => {
                        if (qrModal.registration_id) {
                          fetchQRCode(qrModal.id, qrModal.registration_id);
                        } else if (qrModal.id) {
                          fetchQRCode(qrModal.event_id || qrModal.id, qrModal.id);
                        }
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-600">Failed to load QR code</p>
                </div>
              )}
              <Button
                onClick={() => setQrModal(null)}
                variant="secondary"
                className="w-full"
              >
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Profile;